// Import React and hooks for managing component state and lifecycle
import React, { useState, useEffect } from 'react';
// Import navigation hook for programmatic routing
import { useNavigate } from 'react-router-dom';
// Import custom hook to get current database user information
import { useDbUser } from '../hooks/useDbUser';
// Import database functions and Client TypeScript type
import { db, Client } from '../lib/db';
// Import icons for UI elements (add, edit, delete, building, email, phone, arrow)
import { Plus, Edit, Trash2, Building2, Mail, Phone, ChevronRight } from 'lucide-react';
// Import toast notifications for user feedback
import { toast } from 'sonner';

/**
 * Clients Management Page Component
 * 
 * This page allows users to:
 * - View a list of all their clients
 * - Add new clients with contact information
 * - Edit existing client details
 * - Delete clients (with confirmation)
 * - Click on clients to view their detailed dashboard
 * - Filter clients by status (Active, Inactive, Archived)
 * 
 * Features:
 * - CRUD operations for client management
 * - Form validation (name is required)
 * - Status color coding (green=Active, gray=Inactive, red=Archived)
 * - Contact information display (email, phone, company)
 * - Navigation to individual client dashboards
 */
function ClientsPage() {
  // Get current user's database ID and authentication loading state
  const { dbUserId, loading: dbUserLoading } = useDbUser();
  // Hook for programmatic navigation between pages
  const navigate = useNavigate();
  // State to hold the list of clients loaded from database
  const [clients, setClients] = useState<Client[]>([]);
  // State to track if we're currently loading clients from database
  const [loading, setLoading] = useState(true);
  // State to control whether the add/edit client form is visible
  const [showForm, setShowForm] = useState(false);
  // State to hold the client being edited (null means adding new client)
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  // State for form input values - holds all client information fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    notes: '',
    status: 'Active' as 'Active' | 'Inactive' | 'Archived'
  });

  // Effect to load clients when component mounts and user is authenticated
  useEffect(() => {
    if (dbUserId && !dbUserLoading) {
      loadClients();
    }
  }, [dbUserId, dbUserLoading]);

  /**
   * Loads all clients for the current user from the database
   * Updates the clients state and handles loading/error states
   */
  const loadClients = async () => {
    if (!dbUserId) return;
    
    setLoading(true);
    try {
      const clientData = await db.clients.findByUserId(dbUserId);
      setClients(clientData);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles form submission for both creating new clients and updating existing ones
   * Validates required fields and shows success/error messages
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission behavior
    if (!dbUserId) return;

    try {
      // Check if we're editing an existing client or creating a new one
      if (editingClient) {
        await db.clients.update(editingClient.id, dbUserId, formData);
        toast.success('Client updated successfully');
      } else {
        await db.clients.create({
          ...formData,
          user_id: dbUserId,
          position: clients.length
        });
        toast.success('Client created successfully');
      }
      
      resetForm();
      loadClients();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(editingClient ? 'Failed to update client' : 'Failed to create client');
    }
  };

  /**
   * Prepares the form for editing an existing client
   * Populates form fields with client's current data and shows the form
   */
  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setFormData({
      name: client.name,
      email: client.email || '',
      phone: client.phone || '',
      company: client.company || '',
      notes: client.notes || '',
      status: client.status || 'Active'
    });
    setShowForm(true);
  };

  /**
   * Deletes a client after user confirmation
   * Shows confirmation dialog and handles success/error feedback
   */
  const handleDelete = async (client: Client) => {
    if (!dbUserId) return;
    
    if (confirm(`Are you sure you want to delete ${client.name}?`)) {
      try {
        await db.clients.delete(client.id, dbUserId);
        toast.success('Client deleted successfully');
        loadClients();
      } catch (error) {
        console.error('Error deleting client:', error);
        toast.error('Failed to delete client');
      }
    }
  };

  /**
   * Resets the form to its default state
   * Clears all form fields, hides form, and clears editing state
   */
  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      notes: '',
      status: 'Active'
    });
    setEditingClient(null);
    setShowForm(false);
  };

  /**
   * Returns CSS classes for status badge color coding
   * Active = green, Inactive = gray, Archived = red
   */
  const getStatusColor = (status: Client['status']) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-gray-100 text-gray-800';
      case 'Archived': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading screen while authenticating user or loading clients
  if (dbUserLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">Loading clients...</div>
      </div>
    );
  }

  // Render the complete clients management interface
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Page header with title and Add Client button */}
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        {/* Add Client button - opens the form */}
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </button>
      </div>

      {/* Client Form: Conditionally shown for adding/editing clients */}
      {showForm && (
        <div className="bg-white rounded-lg shadow mb-8 p-6">
          {/* Form title changes based on whether we're editing or creating */}
          <h2 className="text-lg font-semibold mb-4">
            {editingClient ? 'Edit Client' : 'New Client'}
          </h2>
          {/* Form with all client input fields */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as Client['status'] })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                  <option value="Archived">Archived</option>
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Notes
              </label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                {editingClient ? 'Update' : 'Create'} Client
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Clients List: Shows all clients or empty state */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Show empty state if no clients, otherwise show client list */}
        {clients.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No clients</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new client.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {clients.map((client) => (
              <div
                key={client.id}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer"
                onClick={() => navigate(`/clients/${client.id}`)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="text-lg font-medium text-gray-900">{client.name}</h3>
                      {/* Status badge with dynamic color based on client status */}
                      <span className={`ml-3 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(client.status)}`}>
                        {client.status}
                      </span>
                    </div>
                    
                    {/* Show company if available */}
                    {client.company && (
                      <div className="flex items-center mt-1 text-sm text-gray-600">
                        <Building2 className="w-4 h-4 mr-1" />
                        {client.company}
                      </div>
                    )}
                    
                    {/* Contact information row - email and phone if available */}
                    <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                      {client.email && (
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 mr-1" />
                          {client.email}
                        </div>
                      )}
                      {client.phone && (
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-1" />
                          {client.phone}
                        </div>
                      )}
                    </div>
                    
                    {/* Show notes if available (truncated to 2 lines) */}
                    {client.notes && (
                      <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                        {client.notes}
                      </p>
                    )}
                  </div>
                  
                  {/* Action buttons: Edit, Delete, and navigation arrow */}
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent clicking row
                        handleEdit(client);
                      }}
                      className="p-2 text-gray-600 hover:text-gray-900"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent clicking row
                        handleDelete(client);
                      }}
                      className="p-2 text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    {/* Right arrow indicating row is clickable */}
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientsPage;
